import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const API_URL = '/api';

// Tier definitions
const TIERS = [
  { name: 'Starter', coreHours: 3, requests: 1000, minCores: 3 },
  { name: 'Basic', coreHours: 12, requests: 5000, minCores: 4 },
  { name: 'Pro', coreHours: 36, requests: 20000, minCores: 6 },
  { name: 'Business', coreHours: 72, requests: 50000, minCores: 8 },
  { name: 'Enterprise', coreHours: 240, requests: 500000, minCores: 16 }
];

function Dashboard() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Token management state
  const [tokens, setTokens] = useState([]);
  const [showNewToken, setShowNewToken] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenPerms, setNewTokenPerms] = useState(['search']);
  const [newTokenExpiry, setNewTokenExpiry] = useState('');
  const [createdToken, setCreatedToken] = useState(null);

  // Live data state
  const [cpuHistory, setCpuHistory] = useState([]);
  const [logs, setLogs] = useState([]);
  const [lastHeartbeat, setLastHeartbeat] = useState(null);

  // Distributed cache stats
  const [cacheStats, setCacheStats] = useState({
    workers_online: 0,
    cached_keys: 0,
    hit_rate: 0,
    stats: { cache_hits: 0, cache_misses: 0, total_queries: 0 }
  });

  // Alliance & Gamification state
  const [myAlliance, setMyAlliance] = useState(null);
  const [rewardsData, setRewardsData] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState(null);

  // Get next tier helper
  const getNextTier = useCallback((coreHours, cores) => {
    for (const tier of TIERS) {
      if (cores >= tier.minCores && coreHours < tier.coreHours) {
        return tier;
      }
    }
    return null;
  }, []);

  // Load account data
  const loadAccount = useCallback(async (hash) => {
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: hash })
      });
      const data = await res.json();

      if (data.success) {
        // Check if this is a home contributor - redirect to home dashboard
        if (data.data.account_type === 'home') {
          navigate('/home-dashboard');
          return;
        }
        setAccount({ account_hash: hash, ...data.data });
      } else {
        localStorage.removeItem('avalw_account_hash');
        navigate('/');
      }
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }, [navigate]);

  // Load tokens
  const loadTokens = useCallback(async (hash) => {
    try {
      const res = await fetch(`${API_URL}/tokens/${hash}`);
      const data = await res.json();
      if (data.success) {
        setTokens(data.tokens || []);
      }
    } catch (err) {
      console.error('Token load error:', err);
    }
  }, []);

  // Load cache stats
  const loadCacheStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/cache/stats`);
      const data = await res.json();
      if (data.success) {
        setCacheStats(data);
      }
    } catch (err) {
      // Cache stats are optional, fail silently
    }
  }, []);

  // Load alliance and rewards data
  const loadAllianceData = useCallback(async (hash) => {
    try {
      // Load alliance info
      const allianceRes = await fetch(`${API_URL}/my-alliance/${hash}`);
      const allianceData = await allianceRes.json();
      if (allianceData.success) {
        setMyAlliance(allianceData.alliance);
      }

      // Load rewards/level info
      const rewardsRes = await fetch(`${API_URL}/rewards/${hash}`);
      const rewardsInfo = await rewardsRes.json();
      if (rewardsInfo.success) {
        setRewardsData(rewardsInfo);
      }
    } catch (err) {
      // Alliance data is optional
    }
  }, []);

  // Initial load
  useEffect(() => {
    const hash = localStorage.getItem('avalw_account_hash');
    if (!hash) {
      navigate('/');
      return;
    }
    loadAccount(hash);
    loadTokens(hash);
    loadCacheStats();
    loadAllianceData(hash);
  }, [navigate, loadAccount, loadTokens, loadCacheStats, loadAllianceData]);

  // Refresh cache stats every 10 seconds
  useEffect(() => {
    const interval = setInterval(loadCacheStats, 10000);
    return () => clearInterval(interval);
  }, [loadCacheStats]);

  // Refresh status more frequently for live data (every 5 seconds)
  useEffect(() => {
    if (!account) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/worker/status/${account.worker_token}`);
        const data = await res.json();

        if (data.status) {
          setAccount(prev => ({
            ...prev,
            worker_status: data.status,
            system_info: data.system_info || prev.system_info,
            core_hours_today: data.core_hours_today ?? prev.core_hours_today,
            tier: data.tier || prev.tier,
            usage_today: data.usage_today ?? prev.usage_today,
            cpu_usage: data.cpu_usage ?? 0,
            memory_usage: data.memory_usage ?? 0
          }));

          // Update CPU history for chart
          setCpuHistory(prev => {
            const newHistory = [...prev, data.cpu_usage || 0];
            return newHistory.slice(-20);  // Keep last 20 points
          });

          // Update logs
          if (data.heartbeat_logs) {
            setLogs(data.heartbeat_logs);
          }

          // Update last heartbeat
          if (data.last_seen) {
            setLastHeartbeat(data.last_seen);
          }
        }
      } catch (err) {}
    }, 5000);  // 5 second refresh for live data

    return () => clearInterval(interval);
  }, [account?.worker_token]);

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('avalw_account_hash');
    navigate('/');
  };

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Copy token
  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    showToast('Token copied to clipboard!', 'success');
  };

  // Add domain
  const addDomain = async () => {
    if (!newDomain.trim()) return;

    setDomainError('');
    try {
      const res = await fetch(`${API_URL}/whitelist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: account.account_hash, domain: newDomain.trim() })
      });
      const data = await res.json();

      if (data.success) {
        setAccount(prev => ({ ...prev, whitelisted_domains: data.whitelisted_domains }));
        setNewDomain('');
      } else {
        setDomainError(data.error || 'Failed to add domain');
      }
    } catch (err) {
      setDomainError('Connection error');
    }
  };

  // Remove domain
  const removeDomain = async (domain) => {
    try {
      const res = await fetch(`${API_URL}/whitelist`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: account.account_hash, domain })
      });
      const data = await res.json();

      if (data.success) {
        setAccount(prev => ({ ...prev, whitelisted_domains: data.whitelisted_domains }));
      }
    } catch (err) {}
  };

  // Create new token
  const createToken = async () => {
    try {
      const res = await fetch(`${API_URL}/tokens/${account.account_hash}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTokenName || 'API Token',
          permissions: newTokenPerms,
          expires_in_days: newTokenExpiry ? parseInt(newTokenExpiry) : null
        })
      });
      const data = await res.json();

      if (data.success) {
        setCreatedToken(data.token);
        loadTokens(account.account_hash);
        setNewTokenName('');
        setNewTokenPerms(['search']);
        setNewTokenExpiry('');
      } else {
        showToast(data.error || 'Failed to create token', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    }
  };

  // Revoke token
  const revokeToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this token?')) return;

    try {
      const res = await fetch(`${API_URL}/tokens/${account.account_hash}/${tokenId}`, {
        method: 'DELETE'
      });
      const data = await res.json();

      if (data.success) {
        loadTokens(account.account_hash);
        showToast('Token revoked successfully', 'success');
      } else {
        showToast(data.error || 'Failed to revoke token', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    }
  };

  // Regenerate primary token
  const regenerateToken = async () => {
    if (!window.confirm('This will invalidate your current worker token. Your worker will need to be restarted with the new token. Continue?')) return;

    try {
      const res = await fetch(`${API_URL}/tokens/${account.account_hash}/regenerate`, {
        method: 'POST'
      });
      const data = await res.json();

      if (data.success) {
        setAccount(prev => ({ ...prev, worker_token: data.worker_token }));
        loadTokens(account.account_hash);
        showToast('Token regenerated! Update your worker with the new token.', 'success');
      } else {
        showToast(data.error || 'Failed to regenerate token', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    }
  };

  // Format time ago
  const timeAgo = (dateStr) => {
    if (!dateStr) return 'Never';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!account) {
    return null;
  }

  const isOnline = account.worker_status === 'online';
  const coreHours = account.core_hours_today || 0;
  const cores = account.system_info?.cores || 0;
  const tier = account.tier;
  const nextTier = getNextTier(coreHours, cores);
  const usage = account.usage_today || 0;
  const limit = tier?.requests_per_day || 0;
  const usagePercent = limit > 0 ? Math.min((usage / limit) * 100, 100) : 0;

  // CPU Chart data
  const cpuChartData = {
    labels: cpuHistory.map((_, i) => ''),
    datasets: [{
      label: 'CPU %',
      data: cpuHistory,
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: 0
    }]
  };

  const cpuChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, max: 100, grid: { color: '#f0f0f0' } },
      x: { display: false }
    },
    animation: { duration: 0 }
  };

  // Core-Hours chart
  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Today'],
    datasets: [{
      label: 'Core-Hours',
      data: [0, 0, 0, 0, 0, 0, coreHours],
      borderColor: '#2563eb',
      backgroundColor: 'rgba(37, 99, 235, 0.1)',
      fill: true,
      tension: 0.4
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, grid: { color: '#f0f0f0' } },
      x: { grid: { display: false } }
    }
  };

  return (
    <div className="dashboard-page">
      {/* Navigation */}
      <nav className="dash-nav">
        <Link to="/" className="nav-logo">
          <span className="logo-text">AVALW</span>
          <span className="logo-badge">Worker</span>
        </Link>
        <div className="nav-center">
          <Link to="/alliances" className="dash-nav-link">Alliances</Link>
          <Link to="/rewards" className="dash-nav-link">Rewards</Link>
        </div>
        <div className="nav-right">
          <div className={`status-badge ${isOnline ? 'online' : 'offline'}`}>
            <span className="status-dot"></span>
            <span>{isOnline ? 'Worker Online' : 'Worker Offline'}</span>
          </div>
          {lastHeartbeat && isOnline && (
            <span className="last-heartbeat">Last heartbeat: {timeAgo(lastHeartbeat)}</span>
          )}
          <button className="btn btn-outline btn-sm" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      <div className="dash-container">
        {/* Header with Tabs */}
        <div className="dash-header">
          <div>
            <h1>Dashboard</h1>
            <p>Monitor your worker contribution and API usage</p>
          </div>
          <div className="dash-tabs">
            <button
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`tab-btn ${activeTab === 'live' ? 'active' : ''}`}
              onClick={() => setActiveTab('live')}
            >
              Live Monitor
            </button>
            <button
              className={`tab-btn ${activeTab === 'alliance' ? 'active' : ''}`}
              onClick={() => setActiveTab('alliance')}
            >
              Alliance
            </button>
            <button
              className={`tab-btn ${activeTab === 'tokens' ? 'active' : ''}`}
              onClick={() => setActiveTab('tokens')}
            >
              API Tokens
            </button>
          </div>
        </div>

        {/* Worker System Info Bar (when online) */}
        {isOnline && account.system_info && (
          <div className="system-info-bar">
            <div className="sys-item">
              <span className="sys-label">CPU Model</span>
              <span className="sys-value">{account.system_info.cpu_model || '-'}</span>
            </div>
            <div className="sys-item">
              <span className="sys-label">Cores</span>
              <span className="sys-value">{account.system_info.cores || '-'}</span>
            </div>
            <div className="sys-item">
              <span className="sys-label">Speed</span>
              <span className="sys-value">{account.system_info.cpu_speed_ghz ? `${account.system_info.cpu_speed_ghz} GHz` : '-'}</span>
            </div>
            <div className="sys-item highlight">
              <span className="sys-label">CPU Usage</span>
              <span className="sys-value">{(account.cpu_usage || 0).toFixed(1)}%</span>
            </div>
            <div className="sys-item">
              <span className="sys-label">Memory</span>
              <span className="sys-value">{(account.memory_usage || 0).toFixed(1)}%</span>
            </div>
            <div className="sys-item">
              <span className="sys-label">Session</span>
              <span className="sys-value">{account.system_info.worker_uptime_hours ? `${account.system_info.worker_uptime_hours.toFixed(1)}h` : '-'}</span>
            </div>
          </div>
        )}

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Current Tier</div>
                <div className="stat-value primary">{tier?.name || 'None'}</div>
                <div className="stat-sub">{tier ? `${tier.requests_per_day.toLocaleString()} requests/day` : 'Run worker to earn tier'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Core-Hours Today</div>
                <div className="stat-value success">{coreHours.toFixed(1)}</div>
                <div className="stat-sub">
                  {nextTier ? `${(nextTier.coreHours - coreHours).toFixed(1)} more for ${nextTier.name}` : (tier ? 'Max tier reached!' : 'Start contributing')}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">API Usage Today</div>
                <div className="stat-value">{usage.toLocaleString()}</div>
                <div className="stat-sub">/ {limit.toLocaleString()} limit</div>
                <div className="stat-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${usagePercent}%` }}></div>
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Whitelisted Domains</div>
                <div className="stat-value warning">{(account.whitelisted_domains || []).length}</div>
                <div className="stat-sub">domains configured</div>
              </div>
            </div>

            {/* Member Level Card */}
            {account.member_level_info && (
              <div className="level-card-container">
                <div className="level-card">
                  <div className="level-card-header">
                    <div className="level-badge-large">
                      <span className="level-number">{account.member_level || 1}</span>
                      <span className="level-label">LEVEL</span>
                    </div>
                    <div className="level-info">
                      <h3>Member Level</h3>
                      <p className="level-co2">{(account.co2_saved_kg || 0).toFixed(2)} kg CO2 saved</p>
                    </div>
                  </div>
                  <div className="level-progress-section">
                    <div className="level-progress-header">
                      <span>Progress to Level {account.member_level_info.next_level || 'MAX'}</span>
                      <span>{account.member_level_info.progress}%</span>
                    </div>
                    <div className="level-progress-bar">
                      <div
                        className="level-progress-fill"
                        style={{ width: `${account.member_level_info.progress}%` }}
                      ></div>
                    </div>
                    {account.member_level_info.next_level && (
                      <p className="level-progress-sub">
                        {(account.member_level_info.co2_needed - account.member_level_info.co2_current).toFixed(2)} kg CO2 needed
                      </p>
                    )}
                  </div>
                  <div className="level-perks-preview">
                    <span className="perks-label">Unlocked Perks:</span>
                    <div className="perks-badges">
                      {(account.unlocked_perks || []).slice(-4).map((perk, i) => (
                        <span key={i} className="perk-badge" title={perk.description}>
                          {perk.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Link to="/rewards" className="level-view-all">
                    View All Perks & Rewards →
                  </Link>
                </div>
              </div>
            )}

            {/* Main Content Grid */}
            <div className="content-grid">
              {/* Left Column */}
              <div className="content-main">
                {/* Usage Chart */}
                <div className="card">
                  <div className="card-header">
                    <h2>Contribution Overview</h2>
                  </div>
                  <div className="chart-container">
                    <Line data={chartData} options={chartOptions} />
                  </div>
                </div>

                {/* Tier Progress */}
                <div className="card">
                  <div className="card-header">
                    <h2>API Tiers (Based on Core-Hours)</h2>
                  </div>
                  <div className="tiers-list">
                    {TIERS.map((t, i) => {
                      const isAchieved = cores >= t.minCores && coreHours >= t.coreHours;
                      const isCurrent = tier?.name === t.name;
                      const canReach = cores >= t.minCores;

                      return (
                        <div key={t.name} className={`tier-row ${isCurrent ? 'current' : ''} ${isAchieved ? 'achieved' : ''} ${!canReach ? 'locked' : ''}`}>
                          <div className="tier-rank">{i + 1}</div>
                          <div className="tier-info">
                            <div className="tier-name">{t.name}</div>
                            <div className="tier-reqs">{t.requests.toLocaleString()} req/day</div>
                          </div>
                          <div className="tier-requirements">
                            <span className="tier-ch">{t.coreHours} CH</span>
                            <span className="tier-cores">{t.minCores}+ cores</span>
                          </div>
                          <div className="tier-status">
                            {isCurrent && <span className="badge current">Current</span>}
                            {isAchieved && !isCurrent && <span className="badge achieved">Achieved</span>}
                            {!canReach && <span className="badge locked">Need {t.minCores} cores</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="tier-formula">
                    <strong>Core-Hours Formula:</strong> Cores x (Speed / 3.0 GHz) x Usage% x Hours
                  </div>
                </div>

                {/* Whitelisted Domains */}
                <div className="card">
                  <div className="card-header">
                    <h2>Whitelisted Domains</h2>
                    <span className="card-hint">Only these domains can use your API token</span>
                  </div>
                  <div className="domains-list">
                    {(account.whitelisted_domains || []).length === 0 ? (
                      <div className="empty-state">
                        <p>No domains whitelisted yet.</p>
                        <p className="hint">Add domains that are allowed to use your API token.</p>
                      </div>
                    ) : (
                      account.whitelisted_domains.map(domain => (
                        <div key={domain} className="domain-item">
                          <span className="domain-name">{domain}</span>
                          <button className="domain-remove" onClick={() => removeDomain(domain)}>
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  {domainError && <div className="input-error">{domainError}</div>}
                  <div className="domain-form">
                    <input
                      type="text"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      placeholder="example.com"
                      onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                    />
                    <button className="btn btn-primary" onClick={addDomain}>Add Domain</button>
                  </div>
                </div>
              </div>

              {/* Right Sidebar */}
              <div className="content-sidebar">
                {/* Credentials */}
                <div className="card">
                  <div className="card-header">
                    <h2>Your Credentials</h2>
                  </div>
                  <div className="credential-box">
                    <label>Account Hash</label>
                    <div className="credential-value">{account.account_hash}</div>
                  </div>
                  <div className="credential-box">
                    <label>Worker Token</label>
                    <div className="credential-value mono">{account.worker_token}</div>
                  </div>
                  <button className="btn btn-secondary btn-full" onClick={() => copyToken(account.worker_token)}>
                    Copy Worker Token
                  </button>
                </div>

                {/* Quick Start */}
                <div className="card">
                  <div className="card-header">
                    <h2>Quick Start</h2>
                  </div>
                  <div className="terminal">
                    <div className="terminal-header">
                      <span className="dot red"></span>
                      <span className="dot yellow"></span>
                      <span className="dot green"></span>
                      <span className="terminal-title">Terminal</span>
                    </div>
                    <pre className="terminal-body">
<span className="comment"># Install worker</span>
npm i -g @avalw/search-worker

<span className="comment"># Run with your token</span>
avalw-worker {account.worker_token.substring(0, 16)}...
                    </pre>
                  </div>
                </div>

                {/* Documentation Links */}
                <div className="card">
                  <div className="card-header">
                    <h2>Documentation</h2>
                  </div>
                  <div className="doc-links">
                    <Link to="/transparency" className="doc-link">
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
                      What Worker Computes
                    </Link>
                    <Link to="/protocol" className="doc-link">
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
                      Protocol Specification
                    </Link>
                    <Link to="/security" className="doc-link">
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
                      Security & Privacy
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* LIVE MONITOR TAB */}
        {activeTab === 'live' && (
          <div className="live-monitor">
            {!isOnline ? (
              <div className="card">
                <div className="empty-state large">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <h2>Worker Offline</h2>
                  <p>Start your worker to see live monitoring data.</p>
                  <div className="terminal" style={{marginTop: '1rem', maxWidth: '400px'}}>
                    <pre className="terminal-body">
npm i -g @avalw/search-worker
avalw-worker {account.worker_token.substring(0, 16)}...
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Live Stats */}
                <div className="live-stats-grid">
                  <div className="live-stat-card">
                    <div className="live-stat-header">
                      <span className="live-indicator"></span>
                      CPU Usage
                    </div>
                    <div className="live-stat-value">{(account.cpu_usage || 0).toFixed(1)}%</div>
                    <div className="live-chart">
                      <Line data={cpuChartData} options={cpuChartOptions} />
                    </div>
                  </div>
                  <div className="live-stat-card">
                    <div className="live-stat-header">Memory Usage</div>
                    <div className="live-stat-value">{(account.memory_usage || 0).toFixed(1)}%</div>
                    <div className="progress-bar large">
                      <div className="progress-fill" style={{ width: `${account.memory_usage || 0}%` }}></div>
                    </div>
                  </div>
                  <div className="live-stat-card">
                    <div className="live-stat-header">Core-Hours</div>
                    <div className="live-stat-value">{coreHours.toFixed(2)}</div>
                    <div className="live-stat-sub">Today's contribution</div>
                  </div>
                  <div className="live-stat-card">
                    <div className="live-stat-header">Health</div>
                    <div className="health-status healthy">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      Healthy
                    </div>
                    <div className="live-stat-sub">Last heartbeat: {timeAgo(lastHeartbeat)}</div>
                  </div>
                </div>

                {/* Distributed Cache Network Stats */}
                <div className="card">
                  <div className="card-header">
                    <h2>Distributed Cache Network</h2>
                    <span className="card-hint">Your contribution to the AVALW network</span>
                  </div>
                  <div className="cache-stats-grid">
                    <div className="cache-stat">
                      <div className="cache-stat-value">{cacheStats.workers_online}</div>
                      <div className="cache-stat-label">Workers Online</div>
                    </div>
                    <div className="cache-stat">
                      <div className="cache-stat-value">{cacheStats.cached_keys?.toLocaleString() || 0}</div>
                      <div className="cache-stat-label">Cached Queries</div>
                    </div>
                    <div className="cache-stat">
                      <div className="cache-stat-value highlight">{cacheStats.hit_rate || 0}%</div>
                      <div className="cache-stat-label">Cache Hit Rate</div>
                    </div>
                    <div className="cache-stat">
                      <div className="cache-stat-value">{cacheStats.stats?.cache_hits?.toLocaleString() || 0}</div>
                      <div className="cache-stat-label">Total Cache Hits</div>
                    </div>
                  </div>
                  <div className="cache-explanation">
                    <p>Your worker stores and serves search results for the AVALW network. When users search on avalw.org, your cached results help speed up responses and reduce server load.</p>
                  </div>
                </div>

                {/* Heartbeat Logs */}
                <div className="card">
                  <div className="card-header">
                    <h2>Heartbeat Logs</h2>
                    <span className="card-hint">Real-time worker activity</span>
                  </div>
                  <div className="logs-container">
                    {logs.length === 0 ? (
                      <div className="empty-state">
                        <p>No logs yet. Waiting for heartbeats...</p>
                      </div>
                    ) : (
                      logs.map((log, i) => (
                        <div key={i} className="log-entry">
                          <span className="log-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                          <span className={`log-status ${log.message.includes('connected') ? 'success' : ''}`}>
                            {log.message}
                          </span>
                          <span className="log-cpu">CPU: {log.cpu_usage?.toFixed(1) || 0}%</span>
                          <span className="log-ch">CH: {log.core_hours?.toFixed(2) || 0}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* API TOKENS TAB */}
        {activeTab === 'tokens' && (
          <div className="tokens-section">
            <div className="card">
              <div className="card-header">
                <h2>API Tokens</h2>
                <button className="btn btn-primary btn-sm" onClick={() => setShowNewToken(true)}>
                  Create New Token
                </button>
              </div>

              {/* Created Token Alert */}
              {createdToken && (
                <div className="token-created-alert">
                  <div className="alert-header">
                    <strong>Token Created Successfully!</strong>
                    <button onClick={() => setCreatedToken(null)}>&times;</button>
                  </div>
                  <p>Save this token now. It won't be shown again.</p>
                  <div className="token-display">
                    <code>{createdToken.token}</code>
                    <button className="btn btn-sm" onClick={() => copyToken(createdToken.token)}>Copy</button>
                  </div>
                </div>
              )}

              {/* Tokens List */}
              <div className="tokens-list">
                {tokens.filter(t => !t.revoked).map(token => (
                  <div key={token.id} className="token-item">
                    <div className="token-info">
                      <div className="token-name">{token.name}</div>
                      <div className="token-preview">{token.token_preview}</div>
                      <div className="token-meta">
                        <span className="token-perms">
                          {token.permissions.join(', ')}
                        </span>
                        {token.expires_at && (
                          <span className="token-expiry">
                            Expires: {new Date(token.expires_at).toLocaleDateString()}
                          </span>
                        )}
                        <span className="token-created">
                          Created: {new Date(token.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="token-actions">
                      {token.name === 'Primary Token' ? (
                        <button className="btn btn-outline btn-sm" onClick={regenerateToken}>
                          Regenerate
                        </button>
                      ) : (
                        <button className="btn btn-danger btn-sm" onClick={() => revokeToken(token.id)}>
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Revoked Tokens */}
              {tokens.filter(t => t.revoked).length > 0 && (
                <>
                  <h3 style={{marginTop: '2rem', marginBottom: '1rem', color: '#64748b'}}>Revoked Tokens</h3>
                  <div className="tokens-list revoked">
                    {tokens.filter(t => t.revoked).map(token => (
                      <div key={token.id} className="token-item revoked">
                        <div className="token-info">
                          <div className="token-name">{token.name}</div>
                          <div className="token-preview">{token.token_preview}</div>
                        </div>
                        <span className="badge revoked">Revoked</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Create Token Modal */}
            {showNewToken && (
              <div className="modal-overlay" onClick={() => setShowNewToken(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <button className="modal-close" onClick={() => setShowNewToken(false)}>
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  </button>
                  <h2>Create New API Token</h2>
                  <p>Create a token with specific permissions for your applications.</p>

                  <div className="form-group">
                    <label>Token Name</label>
                    <input
                      type="text"
                      value={newTokenName}
                      onChange={e => setNewTokenName(e.target.value)}
                      placeholder="My App Token"
                      className="modal-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Permissions</label>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newTokenPerms.includes('search')}
                          onChange={e => {
                            if (e.target.checked) {
                              setNewTokenPerms([...newTokenPerms, 'search']);
                            } else {
                              setNewTokenPerms(newTokenPerms.filter(p => p !== 'search'));
                            }
                          }}
                        />
                        Search API
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newTokenPerms.includes('heartbeat')}
                          onChange={e => {
                            if (e.target.checked) {
                              setNewTokenPerms([...newTokenPerms, 'heartbeat']);
                            } else {
                              setNewTokenPerms(newTokenPerms.filter(p => p !== 'heartbeat'));
                            }
                          }}
                        />
                        Worker Heartbeat
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Expiration (optional)</label>
                    <select
                      value={newTokenExpiry}
                      onChange={e => setNewTokenExpiry(e.target.value)}
                      className="modal-input"
                    >
                      <option value="">Never expires</option>
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                      <option value="365">1 year</option>
                    </select>
                  </div>

                  <button className="btn btn-primary btn-full" onClick={createToken}>
                    Create Token
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ALLIANCE TAB */}
        {activeTab === 'alliance' && (
          <div className="alliance-tab-content">
            {/* Level & Progress Card */}
            {rewardsData && (
              <div className="level-progress-card">
                <div className="level-avatar-section">
                  <div className={`level-avatar frame-${rewardsData.rewards?.active?.frame || 'none'}`}>
                    <span className="level-number-big">{rewardsData.level?.current || 1}</span>
                  </div>
                  <div className="level-details">
                    <h3>Contributor Level</h3>
                    <p className="level-title">{rewardsData.rewards?.active?.title || 'Newcomer'}</p>
                  </div>
                </div>
                <div className="level-progress-section">
                  <div className="level-progress-header">
                    <span>{rewardsData.level?.serves?.toLocaleString() || 0} serves</span>
                    <span>{rewardsData.level?.serves_for_next?.toLocaleString() || 0} for next level</span>
                  </div>
                  <div className="level-progress-bar">
                    <div
                      className="level-progress-fill"
                      style={{ width: `${rewardsData.level?.progress || 0}%` }}
                    ></div>
                  </div>
                </div>
                <div className="level-eco-stats">
                  <div className="eco-stat">
                    <span className="eco-icon">🌿</span>
                    <span className="eco-value">{rewardsData.co2?.kg || 0} kg</span>
                    <span className="eco-label">CO2 Saved</span>
                  </div>
                  <div className="eco-stat">
                    <span className="eco-icon">🎁</span>
                    <span className="eco-value">
                      {(rewardsData.rewards?.unlocked?.frames?.length || 0) +
                       (rewardsData.rewards?.unlocked?.titles?.length || 0)}
                    </span>
                    <span className="eco-label">Rewards</span>
                  </div>
                </div>
                <Link to="/rewards" className="btn btn-outline btn-sm" style={{marginTop: '1rem'}}>
                  View All Rewards
                </Link>
              </div>
            )}

            {/* Alliance Section */}
            <div className="alliance-section-card">
              <h2>
                <span style={{marginRight: '0.5rem'}}>🌍</span>
                My Alliance
              </h2>

              {myAlliance ? (
                <div className="my-alliance-dashboard">
                  <div className="alliance-info-row">
                    <div className="alliance-badge-preview" style={{background: myAlliance.banner_color}}>
                      <span>Lv.{myAlliance.level}</span>
                    </div>
                    <div className="alliance-info-text">
                      <h3>{myAlliance.name}</h3>
                      <p>{myAlliance.is_leader ? '👑 Leader' : `#${myAlliance.my_rank} Member`}</p>
                    </div>
                  </div>
                  <div className="alliance-quick-stats">
                    <div className="quick-stat">
                      <span className="quick-value">{myAlliance.member_count}/100</span>
                      <span className="quick-label">Members</span>
                    </div>
                    <div className="quick-stat eco">
                      <span className="quick-value">{myAlliance.co2_kg} kg</span>
                      <span className="quick-label">CO2 Saved</span>
                    </div>
                    <div className="quick-stat">
                      <span className="quick-value">{myAlliance.total_serve_count?.toLocaleString()}</span>
                      <span className="quick-label">Total Serves</span>
                    </div>
                  </div>
                  <div className="alliance-actions-row">
                    <Link to={`/alliance/${myAlliance.id}`} className="btn btn-primary">
                      View Alliance
                    </Link>
                    <Link to="/alliances" className="btn btn-outline">
                      Browse All
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="no-alliance-state">
                  <div className="no-alliance-icon">🌱</div>
                  <h3>Join an Alliance</h3>
                  <p>Team up with other contributors to maximize your environmental impact</p>
                  <div className="no-alliance-actions">
                    <Link to="/alliances" className="btn btn-primary">
                      Browse Alliances
                    </Link>
                    <Link to="/alliances/create" className="btn btn-outline">
                      Create New
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Eco Impact Summary */}
            <div className="eco-impact-card">
              <h2>
                <span style={{marginRight: '0.5rem'}}>🌿</span>
                Your Eco Impact
              </h2>
              <div className="eco-impact-grid">
                <div className="impact-item">
                  <div className="impact-icon">🔍</div>
                  <div className="impact-value">{rewardsData?.level?.serves?.toLocaleString() || 0}</div>
                  <div className="impact-label">Queries Served</div>
                </div>
                <div className="impact-item highlight">
                  <div className="impact-icon">🌍</div>
                  <div className="impact-value">{rewardsData?.co2?.kg || 0} kg</div>
                  <div className="impact-label">CO2 Saved</div>
                </div>
                <div className="impact-item">
                  <div className="impact-icon">⏱️</div>
                  <div className="impact-value">{((account?.uptime_hours || 0) / 24).toFixed(1)}</div>
                  <div className="impact-label">Days Online</div>
                </div>
              </div>
              <p className="eco-hint">
                Each cached query you serve saves approximately 0.3g of CO2 by reducing redundant computation.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-icon">
            {toast.type === 'success' ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            )}
          </div>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
